// @wc-ignore-file
/**
 * CR-230 S4: the CR "Notion pages" section's data access.
 *
 * - Search and attach go through the api-server routes (contract:
 *   shared/types/notion-context.ts). Attach is server-side: the route re-reads
 *   the page (title, icon, child links) from Notion, so the client only sends
 *   the page id. Membership is checked on the project.
 * - The reference rows themselves are CR data: change-requests-api reads and
 *   removes them (this feature is the Notion transport only).
 * - Read the error body's `code` FIRST: the HTTP status is not a discriminant
 *   (409 is both `notion_not_configured` and `duplicate`; 401 is
 *   `notion_reconnect_required`).
 */
import { invokeFunction } from "@/lib/functions";
import type {
  NotionContextAttachRequest,
  NotionContextAttachSuccessBody,
  NotionContextDuplicateBody,
  NotionContextErrorBody,
  NotionContextSearchRequest,
  NotionContextSearchSuccessBody,
} from "@shared/types/notion-context";
import type { CrNotionReference } from "@shared/types/supabase.types";

/** A route error code (never `duplicate`, which is an outcome), or `network_error`. */
export type NotionClientErrorCode = NotionContextErrorBody["code"] | "network_error";

export class NotionContextError extends Error {
  readonly code: NotionClientErrorCode;
  readonly retryAfterSeconds?: number;

  constructor(code: NotionClientErrorCode, retryAfterSeconds?: number) {
    super(code);
    this.name = "NotionContextError";
    this.code = code;
    this.retryAfterSeconds = retryAfterSeconds;
  }
}

const ROUTE_CODES = new Set<string>([
  "invalid_request",
  "forbidden",
  "notion_not_configured",
  "notion_reconnect_required",
  "notion_rate_limited",
  "notion_page_unavailable",
  "notion_unavailable",
  "cr_not_found",
]);

/** A code this client knows (route codes, plus `network_error`). */
export function isNotionClientErrorCode(code: string): code is NotionClientErrorCode {
  return code === "network_error" || ROUTE_CODES.has(code);
}

/**
 * Map an invokeFunction error to a stable code. The membership middleware
 * answers `{ error }` without a code, so its refusal is read from the status.
 */
export function toNotionContextError(error: {
  status?: number;
  body?: unknown;
}): NotionContextError {
  const body = error.body as Partial<NotionContextErrorBody> | null | undefined;
  if (body && typeof body.code === "string" && ROUTE_CODES.has(body.code)) {
    const retryAfter =
      typeof body.retryAfterSeconds === "number" && Number.isFinite(body.retryAfterSeconds)
        ? body.retryAfterSeconds
        : undefined;
    return new NotionContextError(body.code as NotionClientErrorCode, retryAfter);
  }
  if (error.status === undefined) return new NotionContextError("network_error");
  if (error.status === 401 || error.status === 403) return new NotionContextError("forbidden");
  if (error.status === 400) return new NotionContextError("invalid_request");
  return new NotionContextError("notion_unavailable");
}

export async function searchNotionPages(
  request: NotionContextSearchRequest,
): Promise<NotionContextSearchSuccessBody> {
  const { data, error } = await invokeFunction<NotionContextSearchSuccessBody>(
    "notion-context-search",
    { body: request },
  );
  if (error || !data) throw toNotionContextError(error ?? {});
  return data;
}

/** An attach that wrote nothing because the page is already on the CR. */
interface NotionAttachDuplicate {
  status: "duplicate";
  existing: CrNotionReference;
}

export type NotionAttachOutcome =
  | Pick<NotionContextAttachSuccessBody, "status" | "reference">
  | NotionAttachDuplicate;

/**
 * Attach (or, with mode `update`, re-read) one page. A duplicate is returned,
 * not thrown: it is an expected outcome the section answers with Keep / Update.
 */
export async function attachNotionPage(
  request: NotionContextAttachRequest,
): Promise<NotionAttachOutcome> {
  const { data, error } = await invokeFunction<NotionContextAttachSuccessBody>(
    "notion-context-attach",
    { body: request },
  );
  if (error) {
    const body = error.body as Partial<NotionContextDuplicateBody> | null | undefined;
    if (body?.code === "duplicate" && body.existing) {
      return { status: "duplicate", existing: body.existing };
    }
    throw toNotionContextError(error);
  }
  if (!data) throw new NotionContextError("notion_unavailable");
  return { status: data.status, reference: data.reference };
}

/**
 * A Notion link the page may render as an href, or null. The server only
 * stores notion.so URLs (CHECK + route validation); this is the belt to those
 * braces, so a stored value can never become a `javascript:` link.
 */
export function safeNotionUrl(url: string | null | undefined): string | null {
  if (typeof url !== "string") return null;
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== "https:") return null;
    if (parsed.hostname !== "notion.so" && parsed.hostname !== "www.notion.so") return null;
    // Same rule as the server's normaliser: no credentials, no explicit port.
    if (parsed.username || parsed.password || parsed.port) return null;
    return parsed.href;
  } catch {
    return null;
  }
}
